import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { SupportedLocale } from './localized-string';

export type ProductLocaleMap = Partial<Record<SupportedLocale, string>>;

export interface ProductSummary {
  id: string;
  code: string;
  displayName: string;
  /** Sidecar translations for `displayName`. Null when none are authored. */
  displayNameTranslations: ProductLocaleMap | null;
  status: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  assignment_count: number;
}

export interface ProductDetail extends ProductSummary {
  grant_count: number;
  assignments: Array<{
    organisationId: string;
    organisationName: string;
    status: string;
    assignedAt: string;
  }>;
}

export interface CreateProductInput {
  code: string;
  display_name: string;
  display_name_translations?: ProductLocaleMap;
}

export interface UpdateProductInput {
  display_name?: string;
  display_name_translations?: ProductLocaleMap;
  status?: string;
}

export interface OrgProductAssignment {
  id: string;
  organisationId: string;
  productId: string;
  productCode: string;
  productDisplayName: string;
  status: string;
  actorContextJwksUrl: string | null;
  actorContextIssuer: string | null;
  actorContextAudience: string | null;
  assignedAt: string;
  assignedByActor: Record<string, unknown>;
  revokedAt: string | null;
  revokedByActor: Record<string, unknown> | null;
}

export interface AssignProductInput {
  product_id: string;
  actor_context_jwks_url?: string;
  actor_context_issuer?: string;
  actor_context_audience?: string;
}

export interface UpdateAssignmentInput {
  status?: string;
  actor_context_jwks_url?: string;
  actor_context_issuer?: string;
  actor_context_audience?: string;
}

const KEYS = {
  list: (includeDeleted: boolean) => ['products', 'list', includeDeleted] as const,
  detail: (id: string) => ['products', 'detail', id] as const,
  orgList: (orgId: string, includeRevoked: boolean) =>
    ['org-products', orgId, includeRevoked] as const,
};

export function useProducts(opts: { includeDeleted?: boolean } = {}) {
  const includeDeleted = opts.includeDeleted ?? false;
  return useQuery({
    queryKey: KEYS.list(includeDeleted),
    queryFn: () =>
      apiGet<ProductSummary[]>(
        includeDeleted ? '/api/products?include_deleted=true' : '/api/products',
      ),
  });
}

export function useProduct(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.detail(id) : ['products', 'detail', 'idle'],
    queryFn: () => apiGet<ProductDetail>(`/api/products/${id}`),
    enabled: !!id,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) => apiPost<ProductDetail>('/api/products', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useUpdateProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProductInput) =>
      apiPatch<ProductDetail>(`/api/products/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useDeleteProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete(`/api/products/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

// Per-org assignment hooks — used by /orgs/:id/products and
// MembershipProductsModal (which now sources its candidates from the
// org's active assignments rather than the old per-org Product list).
export function useOrgProductAssignments(
  orgId: string | undefined,
  opts: { includeRevoked?: boolean } = {},
) {
  const includeRevoked = opts.includeRevoked ?? false;
  return useQuery({
    queryKey: orgId ? KEYS.orgList(orgId, includeRevoked) : ['org-products', 'idle'],
    queryFn: () =>
      apiGet<OrgProductAssignment[]>(
        includeRevoked
          ? `/api/orgs/${orgId}/products?include_revoked=true`
          : `/api/orgs/${orgId}/products`,
      ),
    enabled: !!orgId,
  });
}

export function useAssignProduct(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignProductInput) =>
      apiPost<OrgProductAssignment>(`/api/orgs/${orgId}/products`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-products', orgId] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useUpdateAssignment(orgId: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAssignmentInput) =>
      apiPatch<OrgProductAssignment>(`/api/orgs/${orgId}/products/${productId}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-products', orgId] }),
  });
}

export function useRevokeAssignment(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => apiDelete(`/api/orgs/${orgId}/products/${productId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-products', orgId] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useGrantProductAccess(userId: string, membershipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) =>
      apiPost(`/api/platform-users/${userId}/memberships/${membershipId}/products`, {
        product_id: productId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-user', userId] }),
  });
}

export function useRevokeProductAccess(userId: string, membershipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) =>
      apiDelete(`/api/platform-users/${userId}/memberships/${membershipId}/products/${productId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-user', userId] }),
  });
}
